---
title: Validacijska konzola
sidebar_position: 4
description: Validacijska konzola
---

# Validacijska konzola

**Validacijska konzola** uporabnikom omogoča organiziranje, upravljanje in dokončevanje nalog strokovnega pregleda s pomočjo svežnjev (Bundle) paketov (Package) za namene validacije (Validation) in učenja.

## Pregled

**Namen**:

- Zbiranje terenskih opazovanj (Observation) z obiskov na terenu
- Validacija napovedi na podlagi satelitskih podatkov
- Zagotavljanje učnih podatkov za algoritme markerjev
- Podpora preverjanju skladnosti
- Organiziranje nalog strokovnega pregleda v strukturirane pakete
- Spremljanje napredka nalog skozi statusne faze
- Omogočanje sistematične terenske validacije in pregleda skladnosti

**Ključne komponente**:

- **Svežnji**: Poimenovane skupine paketov nalog, sestavljene iz filtrov FOI in definicij vnosov
- **Paketi**: Posamezne dodelitve nalog strokovnjakom; vsak paket pokriva podmnožico FOI iz svežnja
- **Pogled validacije**: Vmesnik za dokončevanje dodeljenih pregledov
- **Poročila** (odvisno od obsega (Scope)): Generiranje poročila PDF za izbrana opazovanja

**Spremljanje statusa** — Paketi prehajajo skozi naslednje faze:

1. **Nedodeljeno (Unassigned)**: Na voljo za dodelitev
2. **V obdelavi (Processing)**: Dodeljeno strokovnjaku, delo poteka
3. **Zaključeno (Finished)**: Pregled končan (vrednost statusa v API je `COMPLETED`; to je pomembno pri filtriranju paketov po statusu)

**Dovoljenja**:
Ustvarjanje svežnja zahteva vlogo **GTC-ADMIN**. Pogled svežnjev, pogled paketov in pogled validacije zahtevajo vsaj dostop **GTC-VIEWER** (samo branje/ogled) ali **GTC-OPERATOR** (pisanje — zagotavljanje uporabniških vnosov).

## Pogled za ustvarjanje svežnja

**Dostop**: Pomaknite se prek zložljivega menija stranskega panela — **Ustvarjanje svežnja**. Zahteva vlogo **GTC-ADMIN**.

![Vmesnik modula Ustvarjanje svežnja](../imgs/bundle-create.png)

Sveženj določa, katera Območja zanimanja (FOI) gredo v pakete in katere vnose morajo strokovnjaki izpolniti za vsako FOI. Gumb **Ustvari** je onemogočen, dokler ni dodan vsaj en filter.

### Korak 1 — Nastavite ime svežnja in velikost paketa

1. Vnesite **ime svežnja** za poznejšo prepoznavo svežnja.
2. Nastavite **velikost paketa** — največje število FOI v posameznem paketu. Sistem ustvari `ceil(število FOI / velikost paketa)` paketov.

**Trdne omejitve: največ 1000 FOI na paket in največ 1000 paketov na sveženj.** Če bi katera koli vrednost presegla 1000, sveženj takoj odpove s statusom **FAILED** — noben paket se ne ustvari. Na primer, nastavitev velikosti paketa na 2000 za 3000 ujemajočih se FOI bi ustvarila 2 paketa po 2000, oba nad omejitvijo na paket, in sveženj odpove. Celoten postopek razrešitve si oglejte v [Status svežnja: FAILED](07-support-troubleshooting.mdx#težava-stanje-izdelave-svežnja-je-failed).

### Korak 2 — Določite definicije vnosov

Definicije vnosov določajo, katera polja obrazca se prikažejo v pogledu validacije za vsako FOI, da lahko uporabniki vnesejo svoje podatke. Dodajte toliko vnosov, kolikor jih potrebujete. Za namene zgolj pregleda, kjer uporabniški vnos ni potreben, lahko ta korak preskočite in ustvarite sveženj brez vnosov — to je uporabno, kadar potrebujete zbirko FOI na podlagi poizvedb (večinoma po markerjih) ali za generiranje poročil PDF, ne da bi zahtevali strokovni vnos.

1. Izberite tip vnosa:

| Tip                                | Polja                                                                                                                                                                                                                                             |
| ---------------------------------- | ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| **Opomba**                         | **Oznaka** (obvezno);<br />potrditveno polje **Obvezno**                                                                                                                                                                                          |
| **Klasifikacija** (Classification) | **Oznaka** (obvezno);<br />dve ali več **možnosti**, vsaka z oznako in **bližnjico na tipkovnici** (1 znak);<br />zastavica **Večkratni izbor**, ki uporabnikom omogoča izbiro več kot ene možnosti s seznama;<br />potrditveno polje **Obvezno** |
| **Opazovanje**                     | **Oznaka** (obvezno);<br />**bližnjica na tipkovnici** (1 znak);<br />**kratica** (1–3 znaki);<br />`#RRGGBB` **barva**                                                                                                                           |
| **Dogodek**                        | **Oznaka** (obvezno);<br />**bližnjica na tipkovnici** (1 znak);<br />**kratica** (1–3 znaki);<br />`#RRGGBB` **barva**                                                                                                                           |

2. Izpolnite polja za izbrani tip.
3. Kliknite **Dodaj**, da dodate definicijo vnosa v sveženj.
4. Ponovite za vsak dodatni vnos.

### Korak 3 — Nastavite filtre svežnja

Filtri določajo, katera FOI bodo vključena. Vsi filtri so združeni z operatorjem **AND**. Pred ustvarjanjem svežnja je potreben vsaj en filter.

#### Seznam FOI

1. Vnesite ID-je FOI kot seznam, ločen z vejicami ali presledki. Vsak ID mora biti v obliki `<SCOPE>.FOI.<ID>`.
2. Kliknite **Dodaj filter**.

Primer:

```
DEMO_FR23.FOI.92289506, DEMO_FR23.FOI.92289498, DEMO_FR23.FOI.92289496, DEMO_FR23.FOI.92289493, DEMO_FR23.FOI.92289485, DEMO_FR23.FOI.92289473, DEMO_FR23.FOI.92289471, DEMO_FR23.FOI.92289466, DEMO_FR23.FOI.92289457, DEMO_FR23.FOI.92289420
```

#### Poizvedba po FOI

1. Izberite **podatkovni nabor FOI**. Podatkovni nabor FOI so izvoženi referenčni podatki, ki so bili uporabljeni za izračun signalov in markerjev. Vsak podatkovni nabor:
   - pokriva določeno časovno okno in
   - vsebuje _posnetek_ FOI in njihovih atributov, kakršni so bili v času izvoza.

   Ker gre za posnetek, se filtriranje po atributu nanaša na vrednosti, _zajete v tem podatkovnem naboru_, ne nujno na trenutne vrednosti v sistemu. Za obsege z enim samim uvozom referenčnih podatkov izbira ni pomembna. Za obsege z več uvozi (in zato več podatkovnimi nabori FOI) izberite tisti podatkovni nabor, katerega časovno okno se najbolje ujema z vašimi merili filtriranja — običajno najnovejši.

2. Izberite **atribut**, po katerem želite filtrirati. Razpoložljivi atributi vključujejo vse standardne atribute FOI `id`, `ref_id`, `parent_id`, `foi_type`, `holding_id`, `crop_id`, `land_use_id`, `geometry`, `elevation`, `slope`, `orientation`, `area`, `perimeter`, `centroid`, `soil_type_id`, `agricultural_practice_id` ter lastne dodatne atribute podatkovnega nabora.

3. Izberite **operacijo**: `EQUALS`, `NOT_EQUALS`, `LESS_THAN`, `GREATER_THAN` ali `IN` (več vrednosti).

4. Vnesite **vrednost** (ali več vrednosti za `IN`). Tip vnosa je odvisen od tipa atributa:
   - Za nizovne atribute gre za besedilni vnos natančne vrednosti niza.
   - Za številske atribute gre za številski vnos (za več podrobnosti glejte spodnji odstavek).
   - Za geometrijo gre za niz WKT (npr. `POLYGON((...))`).
   - Za šifrante (Codelist) gre za izbiro iz iskljivega spustnega seznama vnosov šifranta (za več podrobnosti glejte spodnji odstavek).

5. Kliknite **Dodaj filter**.

**Enote za atribute meritev** (v teh enotah vnašajte navadna števila):

| Atribut       | Enota                                                                      |
| ------------- | -------------------------------------------------------------------------- |
| `area`        | kvadratni metri (m²) — npr. parcela velikosti 2 ha ≈ `20000`               |
| `perimeter`   | metri (m)                                                                  |
| `elevation`   | metri (m), povprečje FOI                                                   |
| `slope`       | odstotki (%), povprečje FOI                                                |
| `orientation` | stopinje (°), azimut — 0° = sever, narašča v smeri urinega kazalca (0–360) |

**Filtriranje atributov šifrantov po kodi:**
Ko izberete atribut, podprt s šifrantom (`foi_type`, `crop_id`, `land_use_id`, `soil_type_id` ali `agricultural_practice_id`), se polje **vrednost** spremeni v iskljiv **spustni seznam** vnosov tega šifranta, prikazanih z njihovimi človeku berljivimi oznakami (polno kvalificiranega ID-ja, kot je `DEMO_FR23.CL_CROP.VRC`, ne potrebujete več). S tipkanjem filtrirajte seznam bodisi po oznaki bodisi po kratki kodi (npr. `VRC`), za operacijo `IN` pa lahko izberete več vrednosti. Z vnosom znane kratke kode se samodejno razreši pravi vnos.

#### Poizvedba po markerjih

1. Izberite **kontekst markerjev** (glejte [Ključni pojmi](01-introduction.mdx#kontekst-markerjev)). Kontekst določa, kateri izračuni markerjev se uporabijo za filtriranje.

2. Izberite **tip markerja** (sistem samodejno razreši ustrezno inferenco).

3. Izberite **atribut** markerja.

4. Izberite **operacijo**: `EQUALS`, `NOT_EQUALS`, `LESS_THAN`, `GREATER_THAN` ali `IN` (več vrednosti).

5. Vnesite **vrednost**:
   - Za nizovne atribute gre za besedilni vnos natančne vrednosti niza.
   - Za številske atribute gre za številski vnos (za več podrobnosti glejte spodnji odstavek).

6. Kliknite **Dodaj filter**.

**Kako se vrednotijo filtri atributov markerjev?** Filter se izvede proti inferenčnemu podatkovnemu naboru, ki ustreza izbranemu **kontekstu markerjev** in **tipu markerja**. Ta podatkovni nabor pokriva določeno časovno okno — isti kontekst in časovni razpon, prikazan na zavihku **Povzetek markerjev** v pogledu FOI ali pogledu validacije, kadar imate ta kontekst aktiven. To pomeni, da je število zadetkov filtra enako številu, vidnemu v povzetku markerjev **za ta kontekst**. Če je bil filter ustvarjen z drugim kontekstom markerjev, kot je izbran v možnostih obsega, se rezultati markerjev, prikazani na zavihku Povzetek markerjev v pogledu FOI ali pogledu validacije, ne bodo nujno ujemali z rezultati filtra.

Primer: če filtrirate po atributu števila opazovanj markerja gole zemlje na vrednost `2`, sveženj vključi le tista FOI, ki imajo natanko dve zaznavi gole zemlje znotraj časovnega okna inference za izbrani kontekst — kar se ujema s tem, kar bi videli v **povzetku markerjev pogleda FOI** za ta kontekst.

##### Atributi markerjev po tipu markerja

Kateri atributi se prikažejo v spustnem seznamu **atribut**, je odvisno od **družine (skupine) tipa markerja**. Prikazana imena so človeku berljive oznake — storitev markerjev vsak surovi izhodni stolpec markerja preslika v oznako, tako da z imeni surovih stolpcev ne delate neposredno. Spodnje tabele za vsako družino navajajo atribute, po katerih običajno filtrirate, kaj pomenijo in katere vrednosti vsebujejo.

Vsak tip markerja izpostavlja tudi tri sistemska polja — **ID FOI**, **ID markerja** in **Status** (`0` = OK, `1` = izvedba ni uspela, `2` = ni izvedeno, `3` = izvedeno, vendar nič zaznano, za markerje dogodkov). Običajno filtrirate po spodaj navedenih atributih, specifičnih za markerje, ne po teh.

**Markerji agregirane vrednosti** (npr. povprečni NDVI)

| Atribut           | Predstavlja                                                 | Vrednosti |
| ----------------- | ----------------------------------------------------------- | --------- |
| Vrednost markerja | Agregirana vrednost signala v obdobju (npr. povprečni NDVI) | število   |

**Gola zemlja**

| Atribut                                      | Predstavlja                                    | Vrednosti    |
| -------------------------------------------- | ---------------------------------------------- | ------------ |
| Število opazovanj                            | Skupno število opazovanj gole zemlje v obdobju | celo število |
| Opazovanja markerjev — zaupanje (Confidence) | Verjetnost gole zemlje za posamezno opazovanje | 0–1          |
| Opazovanja markerjev — datum                 | Datum opazovanja gole zemlje                   | datum        |

**Markerji štetja** (npr. število dogodkov košnje)

| Atribut | Predstavlja                         | Vrednosti    |
| ------- | ----------------------------------- | ------------ |
| Število | Število zaznanih dogodkov/opazovanj | celo število |

**Klasifikacijski markerji** — Skupina posevkov, Skupina pokrovnosti tal, Klasifikacija posevkov, Podobnost, Razdalja

| Atribut                                  | Predstavlja                                                        | Vrednosti    |
| ---------------------------------------- | ------------------------------------------------------------------ | ------------ |
| Klasificirano kot                        | Napovedana / najbolj podobna koda razreda                          | koda razreda |
| Deklarirano kot                          | Deklarirana koda razreda (ni prisotna pri skupini pokrovnosti tal) | koda razreda |
| Ocena klasifikacije                      | Zaupanje napovedanega razreda                                      | 0–100        |
| Ocene markerjev — hipoteza               | Kandidatni razred, ki se ocenjuje                                  | koda razreda |
| Ocene markerjev                          | Ocena danega hipoteznega razreda                                   | 0–100        |
| Št. FOI z istim posevkom (samo Razdalja) | Sosedi z istim deklariranim posevkom, uporabljeni za primerjavo    | celo število |

**Homogenost**

| Atribut                                      | Predstavlja                       | Vrednosti                       |
| -------------------------------------------- | --------------------------------- | ------------------------------- |
| Klasificirano kot                            | Rezultat na ravni FOI             | `homogeneous` / `heterogeneous` |
| Ocena klasifikacije                          | Zaupanje rezultata                | 0–100                           |
| Ocene markerjev — hipoteza / Ocene markerjev | Oznaka in ocena za vsako hipotezo | oznaka / 0–100                  |

**Odločitve (Decision) (semafor)**

| Atribut                        | Predstavlja                                   | Vrednosti |
| ------------------------------ | --------------------------------------------- | --------- |
| Koda odločitve                 | Koda izida odločitve                          | koda      |
| Koda barve semaforja odločitve | Dodeljena barva (npr. zelena/rumena/rdeča)    | koda      |
| Koda pasu odločitve            | Kateri pas/pot odločitve je ustvaril rezultat | koda      |

**Ocena (Evaluation)**

| Atribut | Predstavlja                  | Vrednosti    |
| ------- | ---------------------------- | ------------ |
| Ocena   | Ali FOI ustreza izrazu ocene | true / false |

**Markerji dogodkov** — Košnja, Oranje in podobno

| Atribut                                     | Predstavlja                                        | Vrednosti    |
| ------------------------------------------- | -------------------------------------------------- | ------------ |
| Datum začetka dogodka / Datum konca dogodka | Kdaj se zaznani dogodek začne / konča              | datum        |
| Trajanje markerja                           | Dolžina dogodka                                    | dnevi        |
| Število opazovanj markerja                  | Veljavna opazovanja znotraj okna dogodka           | celo število |
| Začetni NDVI / Končni NDVI / Najnižji NDVI  | NDVI na začetku, koncu in v najnižji točki dogodka | število      |
| Padec NDVI                                  | Velikost padca NDVI                                | število      |

Generični markerji dogodkov dodatno izpostavljajo podrobne časovne žige in vrednosti za posamezne točke (časovne žige dogodka/praga/ekstremov in njihove vrednosti FEATURE ter verjetnost dogodka) za podrobnejše poizvedbe.

**Združena košnja**

| Atribut                             | Predstavlja                                           | Vrednosti               |
| ----------------------------------- | ----------------------------------------------------- | ----------------------- |
| Datum začetka / Datum konca dogodka | Okno združenega dogodka                               | datum                   |
| Verjetnost dogodka                  | Zaupanje združenega dogodka                           | število                 |
| Število vnosov / Vnosi              | Koliko izvornih markerjev je bilo združenih in kateri | celo število / besedilo |

**Ozelenitev in žetev**

| Atribut                                                   | Predstavlja                          | Vrednosti |
| --------------------------------------------------------- | ------------------------------------ | --------- |
| Datum začetka/konca ozelenitve; Datum začetka/konca žetve | Okni dogodkov ozelenitve in žetve    | datum     |
| Trajanje ozelenitve / Trajanje žetve                      | Dolžina vsake faze                   | dnevi     |
| Začetni NDVI, končni NDVI, padec NDVI ozelenitve/žetve    | NDVI na mejah faze in velikost padca | število   |

**Košnja na ravni pikslov**

| Atribut                                                        | Predstavlja                                           | Vrednosti          |
| -------------------------------------------------------------- | ----------------------------------------------------- | ------------------ |
| Zaznana košnja                                                 | Ali je bila zaznana kakršna koli košnja               | true / false       |
| Število pikslov z zaznano košnjo                               | Absolutno število pokošenih pikslov                   | celo število       |
| Delež pikslov z zaznano košnjo                                 | Pokošeni piksli kot delež FOI                         | 0–1                |
| Maks. število / Maks. delež povezanih pikslov z zaznano košnjo | Največja strnjena pokošena površina (število / delež) | celo število / 0–1 |
| Število vseh pikslov FOI                                       | Skupno število pikslov v FOI                          | celo število       |

**Čiščenje drogov**

| Atribut                         | Predstavlja                              | Vrednosti    |
| ------------------------------- | ---------------------------------------- | ------------ |
| Število opazovanj               | Skupno število zaznav čiščenja           | celo število |
| Opazovanja markerjev — datum    | Napovedani datum čiščenja                | datum        |
| Opazovanja markerjev — zaupanje | Verjetnost, da je območje droga očiščeno | 0–1          |

### Korak 4 — Ustvarite sveženj

1. Preglejte vse nastavitve.
2. Kliknite **Ustvari**.

Sveženj se ustvari s statusom **CREATED**, med generiranjem paketov preide v **PROCESSING**, nato pa doseže **DONE** (ali **FAILED** ob napaki). Paketi so na voljo takoj, ko sveženj doseže **DONE**.

## Pogled svežnjev

**Dostop**: Pomaknite se prek zložljivega menija stranskega panela — **Svežnji**.

![Modul Svežnji s seznamom svežnjev](../imgs/bundle-list.png)

Pogled svežnjev našteva vse svežnje v obsegu. Stolpci vključujejo ime svežnja, datum ustvarjanja, število paketov in status svežnja. Seznam lahko filtrirate in razvrščate.

**Dejanja za posamezen sveženj**:

- **Uredi ime**: Preimenujte sveženj.
- **Odpri**: Pomaknite se v pogled paketov, filtriran na ta sveženj.
- **Izbriši**: Izbrišite sveženj, njegove pakete in vse povezane podatke. Pred izbrisom se prikaže potrditveno pogovorno okno.

## Pogled paketov

**Dostop**: Pomaknite se prek zložljivega menija stranskega panela — **Paketi**.

![Vmesnik modula Paketi](../imgs/packages-view.png)

Pogled paketov našteva vse pakete iz vseh svežnjev. Filtrirajte po statusu, dodeljenem uporabniku, ID-ju svežnja ali ID-ju paketa.

**Izbiranje paketov**: Z klikom na vrstico jo izberete. Pridržite **CTRL** (ali **CMD** na Macu) za dodajanje ali odstranjevanje posameznih vrstic — tako narejeni izbori se ohranijo med stranmi. Pridržite **SHIFT** za izbiro strnjenega obsega; izbira obsega s SHIFT deluje le znotraj trenutne strani (sidro se ponastavi, ko spremenite stran).

**Dejanja, na voljo iz orodne vrstice**:

- **Dodeli / Ponovno dodeli**: Dodelite nedodeljene pakete ali ponovno dodelite že dodeljene pakete izbranemu uporabniku.
- **Izvozi v CSV**: Izvozite podatke paketov v datoteko CSV (ena datoteka `.csv` na izbrani paket).
- **Izbriši**: Izbrišite paket in vse njegove zbrane podatke (uporabniške vnose). Pred izbrisom potrdite v pogovornem oknu.

**Dejanja za posamezno vrstico** (stolpec Dejanja):

- **Odpri paket** (ikona fokusa): Odprite paket neposredno v pogledu validacije.

## Pogled validacije

**Dostop**: Pomaknite se prek zložljivega menija stranskega panela — **Pogled validacije**, ali odprite paket neposredno iz pogleda paketov.

**Opomba**: Pogled validacije si deli komponente z modulom pogleda FOI (glejte [Modul Pogled FOI](03-foi-view-module.mdx)) — gradnik opazovanj, gradnik zemljevida in vizualizacijo signalov/markerjev. Ta razdelek pokriva delovni postopek, specifičen za validacijo.

![Pogled validacije](../imgs/validation-view.png)

Glava aplikacije prikazuje trenutni obseg, **ID svežnja** in **ID paketa**, medtem ko delate znotraj paketa. S klikom na **ID svežnja** se odpre spustni meni z dvema možnostma: **"Glej sveženj v pogledu Svežnji"** (pomakne se v pogled svežnjev, filtriran na ta sveženj) in **"Glej pakete svežnja v pogledu Paketi"** (pomakne se v pogled paketov, filtriran na pakete tega svežnja). S klikom na **ID paketa** se odpre spustni meni z eno možnostjo: **"Glej paket v pogledu Paketi"** (pomakne se v pogled paketov, filtriran na ta konkretni paket).

### Izbiranje FOI

1. Ko je paket odprt, levi stolpec prikaže seznam FOI v tem paketu.
2. Kliknite FOI na seznamu. Naloži se v vmesnik za pregled.

### Pregledovanje podatkov

Za izbrano FOI preglejte:

- **Opazovanja**: Satelitski posnetki v časovnici
- **Markerji**: Napovedi in vrednosti zaupanja
- **Signali**: Časovne vrste signalov v grafu
- **Zemljevid**: Prostorski kontekst in sosednja FOI

### Tipi vnosnih obrazcev

Pod seznamom FOI levi stolpec prikazuje vnosne obrazce, določene za ta sveženj.

#### Vnosi opomb

1. V besedilno polje vnesite opombo.
2. Kliknite **Shrani**.

#### Vnosi klasifikacije

1. Izberite eno možnost iz spustnega seznama klasifikacije (ali več možnosti, če ima vnos omogočen **Večkratni izbor**, ali uporabite bližnjico na tipkovnici, določeno za vsako možnost).
2. Kliknite **Shrani**.

#### Vnosi opazovanj

1. Aktivirajte vnos s klikom nanj v obrazcu ali s pritiskom njegove bližnjice na tipkovnici.
2. V gradniku opazovanj izberite enega ali več datumov za opazovanje.
3. Kliknite **Shrani**.

#### Vnosi dogodkov

1. Aktivirajte vnos s klikom nanj v obrazcu ali s pritiskom njegove bližnjice na tipkovnici.
2. V gradniku opazovanj izberite datum začetka in konca dogodka.
3. Kliknite **Shrani**.

### Shranjevanje in oddajanje podatkov

| Dejanje      | Rezultat                                                    |
| ------------ | ----------------------------------------------------------- |
| **Shrani**   | Shrani vnesene podatke; FOI ostane v stanju _v obdelavi_    |
| **Končano**  | Shrani podatke in označi FOI kot _dokončano_                |
| **Preskoči** | Označi FOI kot _preskočeno_ in se premakne na naslednjo FOI |

### Dokončevanje paketa

1. Po pregledu vseh FOI:
2. Kliknite **Dokončaj paket**.
3. Potrdite dokončanje v pogovornem oknu.
4. Status paketa se spremeni v **Zaključeno** (vrednost API: `COMPLETED`) in paket se zapre.

Pred dokončanjem morajo biti vsa FOI pregledana (dokončana ali preskočena). Manjkajoče odločitve bodo označene.

## Poročilo PDF

![Zavihek Poročila v pogledu validacije, ki prikazuje poziv za izbiro opazovanj, polji Identifikator ocene in Prosto besedilo ter gumb GENERIRAJ PDF](../imgs/validation-console-reports-tab.png)

Zavihek **Poročila** se v levem stolpcu pogleda validacije prikaže le, kadar ima obseg omogočeno generiranje poročil PDF (`reporting.pdfGeneration.show = true` v konfiguraciji obsega). Če zavihek ni viden, vaš obseg te funkcije nima aktivne.

### Generiranje poročila

1. V pogledu validacije kliknite zavihek **Poročila** v levem stolpcu.
2. V gradniku opazovanj izberite **1–6 opazovanj**. Ob izbiri vsakega se na njegovi ploščici opazovanja prikaže modra ikona PDF, na grafu signalov/markerjev pa modri navpični trak. Števec **N/6** na zavihku Poročila se posodablja ob vsaki izbiri.
3. Izpolnite polje **Identifikator ocene** (obvezno).
4. Po želji izpolnite polje **Prosto besedilo**.
5. Kliknite **GENERIRAJ PDF**. Gumb je omogočen le, kadar je identifikator neprazen, izbrano je 1–6 opazovanj in nobeno generiranje ne poteka. PDF se po pripravljenosti samodejno prenese.
